import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
// import { useWindowSize } from 'react-use';
import { InsAccountModel, SecondaryTemplateDataModel } from '@/types/home';
import { debounce } from 'lodash-es';
import { useUserProvider } from '@/context/UserProvider';
import { fetchInstagramAnonymouslySearchData } from '@/lib/service/anonymously';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import LocalImage from '../Common/LocalImage';
import { message } from 'antd';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import CommonSimpleModal from '../Common/CommonSimpleModal';
import { useRouter } from 'next/router';
import LandingPagePricingContent from './PricingContent';
import { AccountLimitAlert, LimitAlert } from './FeeLimitAlert';
import { addInsStory } from '@/lib/service/dashboard';
import CustomersTalkUsContent from './CustomersTalkUsContent';
import useGuestViewerSearchUsageState from '@/lib/hooks/common/useGuestViewerSearchUsageState';
import useUserViewerSearchUsageState from '@/lib/hooks/common/useUserViewerSearchUsageState';
import InstagramStoresFree from '../view-instagram-anonymously/InstagramStoresFree';
import UserDashboard from '../Dashboard/UserDashboard';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';

const FAQContent = dynamic(() => import('./FAQContent'), { ssr: true });
const DownloadInstagramContent = dynamic(() => import('./DownloadInstagramContent'), { ssr: true });
const TopSearchContent = dynamic(() => import('./TopSearch'), { ssr: true });
const GeneralExampDownloadContent = dynamic(() => import('./GeneralExampDownloadContent'), {
  ssr: true,
});
const BestTipsContent = dynamic(() => import('./BestTipsContent'), { ssr: true });
const DownloadStoriesBottomContent = dynamic(() => import('./DownloadStoriesBottomContent'), {
  ssr: true,
});
const MainBenefitsContent = dynamic(() => import('./MainBenefits'), { ssr: true });
const RelatedTools = dynamic(() => import('./RelatedTools'), { ssr: true });
const SearchResultContent = dynamic(() => import('./SearchResultContent'), { ssr: true });

const DownloadInstagramStory = ({ data }: { data?: any }) => {
  const isLockClick = useRef(false);
  const [contentData, setContentData] = useState<SecondaryTemplateDataModel>(data?.contentData);
  const [currentModel, setCurrentModel] = useState<InstagramAnonymouslyModel>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const router = useRouter();
  const [errorCode, setErrorCode] = useState<string | number | null>(null);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const { userUsage, setUserUsage, consumeUserFreeTimes, fetchTimesRequest, syncRemainingTimesData } =
    useUserViewerSearchUsageState();
  const { guestUsage, setGuestUsage, consumeFreeTimes } = useGuestViewerSearchUsageState({
    search_limit: 1,
    search_count: 0,
  });
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [loginState, setLoginState] = useState(isLogin);
  const [inputVisible, setInputVisible] = useState(true);
  const priceRef = useRef<HTMLDivElement>(null);
  const [showLimitPage, setShowLimitPage] = useState(false);
  const [showAccountLimit, setShowAccountLimit] = useState(false);
  const [insListLoading, setInsListLoading] = useState(false);
  const { isPremium } = useStoryPremium();

  const isGuestLocked = useMemo(() => {
    return guestUsage?.search_limit === 0 && guestUsage?.search_count >= 1;
  }, [guestUsage]);

  const isLocked = useMemo(() => {
    if (isLogin) {
      return (
        userUsage?.search_limit === 0 &&
        !userUsage?.first_free_auto_search &&
        !userUsage?.first_free_search &&
        !userUsage.un_limit
      );
    } else {
      return isGuestLocked;
    }
  }, [userUsage, isLogin, isGuestLocked]);

  useEffect(() => {
    if (router.asPath.includes('#price')) {
      setTimeout(() => {
        if (priceRef.current) {
          const targetTop = priceRef.current.offsetTop - 40;
          window.scrollTo({
            top: targetTop,
            behavior: 'smooth',
          });
        }
      }, 800);
    } else if (router.asPath.includes('#media_name')) {
      const hashParams = new URLSearchParams(router.asPath.split('#media_name')[1]);
      const mediaName = hashParams.get('media_name');

      if (!!mediaName) {
        setTimeout(() => {
          onSearch(mediaName);
        }, 800);
      } else {
        setTimeout(() => {
          if (priceRef.current) {
            const targetTop = priceRef.current.offsetTop - 40;
            window.scrollTo({
              top: targetTop,
              behavior: 'smooth',
            });
          }
        }, 500);
      }
    }
  }, [router.asPath]);

  useEffect(() => {
    setContentData(data?.contentData);
  }, [data]);

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value) => {
        setLoginState(value);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

  useEffect(() => {
    if (loginState) {
      fetchTimesRequest(() => {
        if (!!currentModel && isGuestLocked) {
          // 登录时，同步剩余次数
          syncRemainingTimesData(loginState);
        }
      }, loginState);
    }
  }, [loginState, isGuestLocked]);

  useEffect(() => {
    setLoginState(isLogin);

    if (!isLogin) {
      setCurrentModel(null);
    } else {
      if (searchStr && isLockClick.current) {
        onSearchClick(searchStr);
      }
    }
  }, [isLogin]);

  const onValueChanged = (e?: string) => {
    setSearchStr(e ?? '');
  };

  const onSearch = (e?: string) => {
    if (e !== searchStr && !currentModel) {
      setCurrentModel(null);
    }
    setSearchStr(e ?? '');

    // 增加未登录免费试用后，搜索时不再检查登录状态
    onSearchClick(e);
  };

  const onSearchClick = (e?: string) => {
    if (e?.length > 0) {
      if (loading) {
        return;
      }
      // 如果e存在/reel/，就弹窗
      if (e.includes('/reel/')) {
        setAlertInfo({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
          type: 'Instagram-Account-Not-Found',
        });
        return;
      }
      setLoading(true);
      fetchInstagramAnonymouslySearchData(e, null, isLogin ? 0 : isGuestLocked ? 1 : 0, {
        isPremium,
        bizFuncName: 'stories_viewer',
      })
        .then((res) => {
          if (res?.code === 0) {
            if (!isLogin) {
              // 未登录时，消耗免费次数并记录时间
              consumeFreeTimes();
            } else {
              // 登录时，消耗免费次数
              consumeUserFreeTimes();
            }

            setCurrentModel(res?.data);

            if (res.data.private_mark) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.private_mark ? 1 : 0,
                profile_pic_url: res.data.avatar,
                media_name: res.data.media_name,
              };
              setAlertInfo({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });

              return;
            }

            setCurrentModel(res?.data);

            if (searchResultRef.current) {
              const targetTop = searchResultRef.current.offsetTop - 220;
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
            }

            // } else if (res?.code === 22001 || res?.code === 22006) {
          } else if (res?.code === 22001) {
            fetchTimesRequest(undefined, isLogin);
            setShowLimitPage(true);

            // setAlertInfo({
            //   title: t('Out of Views!'),
            //   desc: t("Oops! You've used all your anonymous views for Instagram Stories."),
            //   btnTitle: t('Add Account Now'),
            //   href: `/dashboard`,
            //   type: 'Out-of-Views'
            // });
            // ga_logEvent(EventName.ViewInstagramStoriesAnonymously_OutOfViews)
          } else if (res.code === 22004 || res.code === 22006) {
            fetchTimesRequest(undefined, isLogin);
            setCurrentModel(null);
            setErrorCode(res?.code);
          } else if (res?.code === 22004) {
            fetchTimesRequest(undefined, isLogin);

            setAlertInfo({
              title: t('Private Account Detected'),
              desc: t(
                'The Instagram account you entered is private. DolphinRadar only provides stories and highlights for public accounts. Please enter a public account to continue.'
              ),
              btnTitle: t('Try Another Account'),
              type: 'Private-Account-Detected',
            });
          } else if (res?.code === 22002) {
            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
              type: 'Instagram-Account-Not-Found',
            });
          } else {
            message.error(res?.message);
          }
        })
        .catch((error) => {})
        .finally(() => {
          setLoading(false);
        });
    } else {
      message.error(t('Please enter the insert link or username'));
    }
  };

  const getNoStoriesDetectedContent = (img?: string, name?: string) => {
    return (
      <div style={{ paddingTop: '15px', marginBottom: '50px' }}>
        <p style={{ textAlign: 'center' }}>
          {t(
            'This account hasn’t posted any Stories in the last 24 hours. Add it to your tracking list for long-term monitoring'
          )}
        </p>
        <div
          style={{
            background: '#F1EFFF',
            borderRadius: '24px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          <LocalImage
            src={img}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              overflow: 'hidden',
            }}
          />
          <span style={{ marginLeft: '10px', fontWeight: '500' }}>{name ?? ''}</span>
        </div>
      </div>
    );
  };

  const onAddAccountToTrackClick = (media_name?: string) => {
    if (insListLoading) {
      return;
    }
    setInsListLoading(true);

    addInsStory(media_name)
      ?.then((res) => {
        if (res?.code === 0) {
          message.success(t('Added successfully'));
          router.push('/dashboard?dashboard_type=toolkit');
        } else {
          if (res?.code !== 22008) {
            message.error(res?.message);
          }

          if (res?.code === 22007) {
            setShowLimitPage(true);
          } else if (res?.code === 22008) {
            setShowAccountLimit(true);
          }
        }
      })
      .finally(() => {
        setInsListLoading(false);
      });
  };

  const gotoPlanClick = () => {
    if (priceRef.current) {
      const targetTop = priceRef.current.offsetTop - 60;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // 未登录无免费次数，点击登录
  const handleLogin = () => {
    onOpenLoginModal(true);
    onChangedLoginView(false);
  };

  const handleUnlock = () => {
    isLockClick.current = true;
    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
    } else {
      gotoPlanClick();
    }
  };

  return (
    <>
      <CommonBackContent>
        <TopSearchContent
          topInfo={contentData?.contentInfo?.hero}
          loading={loading}
          leftModel={userUsage}
          guestUsage={guestUsage}
          searchVal={searchStr}
          onSearchClick={onSearch}
          onValueChanged={onValueChanged}
          onInputVisibleChanged={(visible) => setInputVisible(visible)}
          onUnlimitedClick={() => {
            const dom = document.getElementById('free-tools-title');
            if (dom) {
              dom.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          onLoginClick={handleLogin}
          errorCode={errorCode}
          resetErrorCode={() => setErrorCode(null)}
        />

        <div key={'search-result'} ref={searchResultRef}>
          {currentModel && (
            <SearchResultContent
              locked={isLocked}
              loading={insListLoading}
              loadMoreNotCheckLogin={true}
              post_page_token={currentModel?.post_page_token}
              tabFunc={data?.contentData?.function_child}
              data={currentModel}
              onAddAccountToTrackClick={onAddAccountToTrackClick}
              // onPlanClick={gotoPlanClick}
              onPlanClick={() => setShowLimitPage(true)}
              onUnlock={handleUnlock}
            />
          )}
        </div>

        <MainBenefitsContent
          data={contentData?.contentInfo['section-1']}
          onTrackClick={() => {
            scrollToTop();
          }}
        />

        <DownloadInstagramContent data={contentData?.contentInfo?.['section-2']} />
        <GeneralExampDownloadContent data={contentData?.contentInfo?.['section-3']} />

        <div ref={priceRef} id="price">
          <LandingPagePricingContent func="story" />
        </div>
        <InstagramStoresFree
          fetchTimesRequest={() => {
            fetchTimesRequest(undefined, isLogin);
            const dom = document.getElementById('search-now-button');
            if (dom) {
              dom.click();
              dom.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        />

        <CustomersTalkUsContent contentInfo={contentData?.contentInfo?.['section-user-reviews']} />

        {contentData?.faqList && (
          <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        )}
        <RelatedTools data={contentData?.contentInfo?.['section-tool']} />
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </CommonBackContent>

      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        loading={loading}
        searchVal={searchStr}
        inputVisible={inputVisible}
        onValueChanged={onValueChanged}
        onSearchClick={(e) => {
          window.scrollTo({ top: 220 });
          onSearch(e);
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (isOk && alertInfo?.href) {
            if (alertInfo?.type === 'No-Stories-Detected') {
              router.push(
                `/download-from-instagram?media_name=${alertInfo?.media_name}&avatar=${alertInfo?.avatar}&media_url=${alertInfo?.media_url}#1`
              );
              return;
            }
            router.push(alertInfo?.href);
          }
          if (isOk) {
            if ('Out-of-Views' === alertInfo?.type) {
              ga_logEvent(EventName.Story_OutOfViews_AddAccountNow);
            } else {
              setAlertInfo(null);
            }
          }
          setAlertInfo(null);
        }}
      />

      <LimitAlert
        open={showLimitPage}
        closeAction={(isOk) => {
          setShowLimitPage(false);

          if (isOk) {
            ga_logEvent(EventName.GA_search_now_limit_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <AccountLimitAlert
        open={showAccountLimit}
        closeAction={(isOk) => {
          setShowAccountLimit(false);

          if (isOk) {
            ga_logEvent(EventName.GA_account_limit_reached_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />
    </>
  );
};

export default DownloadInstagramStory;
